import React, { useEffect, useState } from 'react';
import { RootState, useSelector } from '../../redux/store';
import type { CellClassRules } from '../../types/common';
import type { ConditionDetailType, ManageConditionType } from '../../types/condition';
import type { CellClassParams } from 'ag-grid-community';
import { CONDITION_VIEW_MODES } from '../../constants';

const useCellClassRulesConditionOrdinary = () => {
  const { condition, selectedViewMode } = useSelector((state: RootState) => state.condition);
  const [cellClassRules, setCellClassRules] = useState<CellClassRules>({});

  useEffect(() => {
    const cellClassRules: CellClassRules = {
      'bg-update': (params: CellClassParams): boolean => {
        // console.log('bg-update', condition, selectedViewMode, cellClassRules);

        if (selectedViewMode === CONDITION_VIEW_MODES.EDITING) {
          // 編集モードの場合（色付けする）
          // ========================================
          const data: ConditionDetailType = params.data;
          const field: string | undefined = params.colDef.field;
          const value: number | boolean | ManageConditionType | string = params.value;
          // 承認されていた場合は承認IDが取得できる
          const approvedDetailId: number | undefined = data.approvedDetailId;
          // 承認されていた場合は承認済みの状態が取得できる
          const orgData: ConditionDetailType | undefined = condition?.detailOrdinaryApprove.find((detail) => detail.id === approvedDetailId);
          // console.log(orgData);

          if (orgData) {
            /**
             * 承認済みデータが見つかれば値の比較を行い判定する
             */
            const orgValue: number | boolean | ManageConditionType | string = orgData[field];
            // console.log(value, orgValue);
            return value !== orgValue;
          } else {
            /**
             * ただし一度削除した後に再度同じ条件を追加し直した場合など、idで照合できないケースもある。この場合は、
             * ①　需要家と受渡場所が全く同じものが承認済みにあった場合は色付けしない
             * ②　承認済みの通常可視制御の中で変更があったものを絞り込めた場合は色付けする
             */
            // ①の場合
            // ----------------------------------------
            // 一度削除してその後同じものを追加した場合、id比較はできないので値でチェックする。
            // 例）承認済みに「需要家：ABC、受渡場所：123」があり、編集モードでこれを一度削除し、それを戻した場合、
            // 戻された「需要家：ABC、受渡場所：123」はidが異なるためid比較では新規登録として色がつくが
            // ユーザから見れば同じものを戻しているため色を付けない（return false）ようにする
            const sameConditionDetail: ConditionDetailType | undefined = condition?.detailOrdinaryApprove?.find((detail) => {
              if (
                // null/''の可能性がある　→　''に揃えて比較する
                (detail.customer ?? '') === (data.customer ?? '') &&
                (detail.deliveryLocation ?? '') === (data.deliveryLocation ?? '')
              ) {
                return true;
              } else {
                return false;
              }
            });
            // 同じものがあれば色付けしない（＝false）
            if (sameConditionDetail !== undefined) {
              // console.log('id not found, but same condition:', data.id, field, value);
              return false;
              // 同じものがなければ②の場合を考える
            } else {
              // console.log('id and same condition not found:', data.id, field, value);
              // ②の場合
              // ----------------------------------------
              // ②-1 まず承認済みの通常可視制御の配列の中からidベースで変更がなかったものを取り除く
              const hasChangeOrdinaries: ConditionDetailType[] | undefined = condition?.detailOrdinaryApprove.reduce<ConditionDetailType[]>((acc, cur) => {
                const sameConditionDetail: ConditionDetailType | undefined = condition?.detailOrdinary?.find((editDetail) => editDetail.approvedDetailId === cur.id);
                // console.log(sameConditionDetail);
                // idが同じものがあり、需要家か受渡場所が同じだったら除外する
                const hasNotChanged: boolean =
                  sameConditionDetail !== undefined &&
                  // null/''の可能性がある　→　''に揃えて比較する
                  (sameConditionDetail.customer ?? '') === (cur.customer ?? '') &&
                  (sameConditionDetail.deliveryLocation ?? '') === (cur.deliveryLocation ?? '');
                if (hasNotChanged) {
                  return acc;
                } else {
                  return [...acc, cur];
                }
              }, []);
              // console.log(hasChangeOrdinaries);
              // ②-2 残りの通常可視制御（変更があったもの）の中から
              // ・需要家のみ一致するもの　あるいは
              // ・受渡場所のみ一致するもの
              // を探して「一つだけ」に絞り込めれば、それとの差分に色付けする
              if (hasChangeOrdinaries?.length) {
                const possibleOrdinaries: ConditionDetailType[] | undefined = hasChangeOrdinaries.filter((detail) => {
                  if (
                    // null/''の可能性がある　→　''に揃えて比較する
                    (detail.customer ?? '') === (data.customer ?? '') ||
                    (detail.deliveryLocation ?? undefined) === (data.deliveryLocation ?? undefined)
                  ) {
                    return true;
                  } else {
                    return false;
                  }
                });
                // console.log(field, value, possibleOrdinaries);
                // 1つだけだったら差分に応じて色付けする。ただし、
                // ・可能性がある承認済み可視制御と全く同じ可視制御があれば新規登録として色付けする
                if (possibleOrdinaries?.length === 1) {
                  const possibleOrdinary: ConditionDetailType = possibleOrdinaries[0];
                  const isPossibleOrdinaryExisting: boolean = Array.isArray(condition?.detailOrdinary)
                    ? condition.detailOrdinary.find((detail) => {
                        if (
                          // null/''の可能性がある　→　''に揃えて比較する
                          (detail.customer ?? '') === (possibleOrdinary.customer ?? '') &&
                          (detail.deliveryLocation ?? undefined) === (possibleOrdinary.deliveryLocation ?? undefined)
                        ) {
                          return true;
                        } else {
                          return false;
                        }
                      }) !== undefined
                    : false;
                  const hasDiff: boolean =
                    field === 'customer'
                      ? (possibleOrdinaries[0].customer ?? '') !== (value ?? '')
                      : field === 'customerName'
                      ? (possibleOrdinaries[0].customerName ?? '') !== (value ?? '')
                      : field === 'deliveryLocation'
                      ? (possibleOrdinaries[0].deliveryLocation ?? '') !== (value ?? '')
                      : field === 'deliveryLocationName'
                      ? (possibleOrdinaries[0].deliveryLocationName ?? '') !== (value ?? '')
                      : false;
                  // console.log(possibleOrdinary, isPossibleOrdinaryExisting, hasDiff);
                  if (isPossibleOrdinaryExisting) {
                    return true;
                  } else {
                    return hasDiff;
                  }
                } else {
                  return true;
                }
              } else {
                return true;
              }
              // それ以外の場合
              // ----------------------------------------
              // サーバ側で何かしら要対応
            }
          }
        } else {
          // 編集モード以外の場合（色付けしない）
          // ========================================
          return false;
        }
      },
    };
    setCellClassRules(cellClassRules);
  }, [condition, selectedViewMode]);

  return { cellClassRules };
};

export default useCellClassRulesConditionOrdinary;
